import { Component } from '@theme/component';
import { CartAddEvent } from '@theme/events';
import { formatShopMoney } from '@theme/money-formatting';

class PdpComparison extends Component {
    #productData = [];
    #syncing = false;
    #abortController = new AbortController();

    /** @type {IntersectionObserver | undefined} */
    #stickyObserver;

    connectedCallback() {
        super.connectedCallback();
        this.#abortController = new AbortController();
        this.#initData();

        document.addEventListener('click', this.#onOutsideClick, { signal: this.#abortController.signal });
        this.#initStickyObserver();
    }

    disconnectedCallback() {
        super.disconnectedCallback();
        this.#abortController.abort();
        this.#stickyObserver?.disconnect();
    }

    /** @param {Event} event */
    #onOutsideClick = (event) => {
        if (this.isConnected && event.target instanceof Node && !this.contains(event.target)) {
            this.#closeAllDropdowns();
        }
    };

    #initStickyObserver() {
        const container = this.querySelector('.desktop-hide .product-display-section');
        if (!container) return;

        // Use IntersectionObserver with a negative top margin to detect when the
        // sticky element actually becomes fixed at the top.
        this.#stickyObserver = new IntersectionObserver(
            ([e]) => {
                container.classList.toggle('js-is-sticky', e.intersectionRatio < 1);
            },
            {
                threshold: [1],
                rootMargin: '-1px 0px 0px 0px', // Trigger slightly before the top
            }
        );

        this.#stickyObserver.observe(container);
    }

    #initData() {
        this.#productData = this.#readComparisonData();
        this.#updateDropdowns();
    }

    #readComparisonData() {
        const dataEl = this.querySelector('script[type="application/json"][data-pdp-comparison-data]');
        if (!dataEl) return [];
        try {
            const parsed = JSON.parse(dataEl.textContent ?? '[]');
            if (!Array.isArray(parsed)) return [];
            return parsed.filter((entry) => Boolean(entry?.product?.handle));
        } catch (error) {
            console.error('[pdp-comparison] failed to parse comparison data', error);
            return [];
        }
    }

    /**
     * @param {string | undefined} handle
     * @param {string | undefined} id
     */
    #findProductData(handle, id) {
        return this.#productData.find((entry) => {
            const product = entry?.product;
            if (!product) return false;
            if (handle && product.handle === handle) return true;
            if (id && String(product.id) === String(id)) return true;
            return false;
        });
    }

    #closeAllDropdowns() {
        this.querySelectorAll('.product-column').forEach((col) => {
            const otherWrapper = col.querySelector('.product-title-wrapper');
            if (otherWrapper) otherWrapper.classList.remove('active');
            const otherDropdown = col.querySelector('.product-dropdown');
            if (otherDropdown) otherDropdown.classList.remove('active');
        });
    }

    toggleDropdown(event) {
        event.stopPropagation();

        if (event.target instanceof Element && event.target.closest('.dropdown-item')) return;

        const column = event.target instanceof Element ? event.target.closest('.product-column') : null;
        if (!column) return;

        const wrapper = column.querySelector('.product-title-wrapper');
        if (!wrapper) return;

        const isActive = wrapper.classList.contains('active');

        this.#closeAllDropdowns();

        if (!isActive) {
            wrapper.classList.add('active');
            const dropdown = column.querySelector('.product-dropdown');
            if (dropdown) dropdown.classList.add('active');
        }
    }

    selectProduct(event) {
        event.stopPropagation();

        const item = event.target instanceof Element ? event.target.closest('.dropdown-item') : null;
        if (!item) return;

        const column = item.closest('.product-column');
        if (!column) return;

        const productId = item.dataset.productId;
        const productHandle = item.dataset.productHandle;

        if (!productHandle && !productId) return;

        if (this.#productData.length === 0) {
            this.#productData = this.#readComparisonData();
        }

        const productData = this.#findProductData(productHandle, productId);
        if (!productData?.product) return;

        column.dataset.productId = String(productData.product.id);
        column.dataset.productHandle = productData.product.handle;

        const wrapper = column.querySelector('.product-title-wrapper');
        if (wrapper) wrapper.classList.remove('active');
        const dropdown = column.querySelector('.product-dropdown');
        if (dropdown) dropdown.classList.remove('active');

        this.#renderProductInColumn(column, productData);
        this.#updateTableRows(column, productData);
        this.#syncProductToSibling(column, productData);
        this.#updateDropdowns();
    }

    selectVariant(event) {
        const input = event.target instanceof Element ? event.target.closest('input') : null;
        if (!input) return;

        const column = input.closest('.product-column');
        const label = input.nextElementSibling;
        if (!column || !label) return;

        const name = label.dataset.name;
        const varId = label.dataset.varid;
        const index = label.dataset.index;

        column.querySelectorAll('.na-form__label').forEach((l) => l.classList.remove('selectedColor'));
        label.classList.add('selectedColor');

        const variantText = column.querySelector('[ref="variantText"]');
        if (variantText) variantText.textContent = name;

        column.querySelectorAll('.customize-product__image').forEach((img) => (img.style.display = 'none'));
        const targetImg = column.querySelector(`.customize-product__image[data-index-${index}]`);
        if (targetImg) targetImg.style.display = 'block';

        this.#updatePrice(column, varId);
        column.dataset.selectedVariant = varId;

        this.#syncVariantToSibling(column, varId, name, index);
    }

    addToCart(event) {
        event.preventDefault();
        event.stopPropagation();

        const button = event.target instanceof Element ? event.target.closest('.add-to-cart-btn') : null;
        if (!button || button.hasAttribute('disabled')) return;

        const column = button.closest('.product-column');
        if (!column) {
            console.warn('ATC: Could not find parent column for button', button);
            return;
        }

        const varId = column.dataset.selectedVariant;
        if (!varId) {
            console.warn('ATC: Missing variant identifier for column', column);
            return;
        }

        const originalText = button.textContent;
        button.textContent = button.dataset.addingText || 'Adding...';
        button.setAttribute('disabled', 'true');

        fetch('/cart/add.js', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ id: varId, quantity: 1 }),
        })
            .then((res) => {
                if (!res.ok) throw new Error(`Cart add failed: ${res.status}`);
                return res.json();
            })
            .then(async () => {
                const cartRes = await fetch(`${window.Shopify?.routes?.root ?? '/'}cart.js`);
                if (!cartRes.ok) throw new Error(`Cart fetch failed: ${cartRes.status}`);
                const cart = await cartRes.json();
                document.dispatchEvent(
                    new CartAddEvent(cart, this.id, {
                        source: 'pdp-comparison',
                        itemCount: cart.item_count,
                        variantId: String(varId),
                    })
                );
            })
            .catch((err) => console.error('ATC Error:', err))
            .finally(() => {
                button.textContent = originalText;
                button.removeAttribute('disabled');
            });
    }

    #updateDropdowns() {
        this.querySelectorAll('.comparison-chart').forEach((chart) => {
            const columns = Array.from(chart.querySelectorAll('.product-column'));
            const selectedIds = columns.map((col) => col.dataset.productId).filter(Boolean);

            columns.forEach((column) => {
                const currentId = column.dataset.productId;
                const otherSelected = selectedIds.filter((id) => id !== currentId);

                column.querySelectorAll('.dropdown-item').forEach((item) => {
                    const itemId = item.dataset.productId;
                    item.classList.toggle('hidden', itemId === currentId || otherSelected.includes(itemId));
                });
            });
        });
    }

    #renderProductInColumn(column, data) {
        const product = data?.product;
        if (!column || !product) return;

        const isPopular = data.is_popular;
        let popularBadge = column.querySelector('.popular-badge');
        if (popularBadge) {
            popularBadge.style.display = isPopular ? '' : 'none';
        }

        column.querySelectorAll('.yotpo-container').forEach((y) => {
            if (String(y.dataset.productId) === String(product.id)) {
                y.classList.remove('hidden');
            } else {
                y.classList.add('hidden');
            }
        });

        const productTitle = column.querySelector('[ref="productTitle"]');
        if (productTitle) productTitle.textContent = product.title;

        column.querySelectorAll('.customize-product__image').forEach((el) => el.remove());
        const insertBefore = column.querySelector('.product-title-wrapper');
        const imageFragment = document.createDocumentFragment();
        const variants = Array.isArray(product.variants) ? product.variants : [];

        variants.forEach((variant, index) => {
            const div = document.createElement('div');
            div.className = 'customize-product__image na-product-card-inline__img';
            div.setAttribute(`data-index-${index}`, '');
            if (index !== 0) div.style.display = 'none';

            const img = document.createElement('img');
            img.src = this.#extractImageUrl(variant, data);
            img.width = 300;
            img.height = 300;
            img.loading = 'lazy';
            img.alt = product.title;
            div.appendChild(img);
            imageFragment.appendChild(div);
        });
        if (insertBefore) {
            column.insertBefore(imageFragment, insertBefore);
        } else {
            column.prepend(imageFragment);
        }

        const container = column.querySelector('.product-form__variant-list');
        if (container) {
            container.innerHTML = '';
            const context = column.closest('.desktop-hide') ? 'mobile' : 'desktop';
            const uniqueVariants = this.#getUniqueVariants(product);
            uniqueVariants.forEach((variant, index) => {
                const swatchEl = this.#createSwatchElement(variant, index, product.handle, data, context);
                container.appendChild(swatchEl);
            });
        }

        const firstVariant = product.variants[0];
        if (firstVariant) {
            column.dataset.selectedVariant = firstVariant.id;
            this.#updatePrice(column, firstVariant.id);
        }

        const atcButton = column.querySelector('.add-to-cart-btn');
        if (atcButton) {
            atcButton.dataset.productId = product.id;
        }
    }

    #updateTableRows(column, data) {
        const chart = column.closest('.comparison-chart');
        if (!chart) return;

        const allColumns = Array.from(chart.querySelectorAll('.product-column'));
        const columnIndex = allColumns.indexOf(column);
        if (columnIndex === -1) return;

        const activeHeaders = Array.from(chart.querySelectorAll('.product-column-header'));
        if (activeHeaders[columnIndex]) {
            activeHeaders[columnIndex].textContent = data.product.title;
            activeHeaders[columnIndex].dataset.productId = data.product.id;
        }

        const allRows = Array.from(chart.querySelectorAll('.table-row'));
        allRows.forEach((row) => {
            const cells = Array.from(row.querySelectorAll('.comparison-cell'));
            if (cells[columnIndex]) {
                const key = row.dataset.feature;
                if (key) {
                    this.#updateCell(cells[columnIndex], data[key]);
                }
            }
        });
    }

    #updateCell(cell, value) {
        cell.innerHTML = '';
        if (Array.isArray(value)) {
            const container = document.createElement('div');
            container.className = 'certification-badges';
            value.forEach((cert) => {
                const badgeWrapper = document.createElement('div');
                badgeWrapper.className = 'cert-badge';

                const img = document.createElement('img');
                img.src = typeof cert === 'string' ? cert : cert.url || cert.src || '';
                img.className = 'certificat-image';
                img.width = 50;
                img.loading = 'lazy';
                img.alt = 'FSC Certificate Image';

                badgeWrapper.appendChild(img);
                container.appendChild(badgeWrapper);
            });
            cell.appendChild(container);
        } else {
            cell.textContent = value ? 'Yes' : 'No';
        }
    }

    #getSiblingColumn(column) {
        const isMobile = !!column.closest('.desktop-hide');
        const sourceChart = column.closest('.comparison-chart');
        const siblingChart = isMobile
            ? this.querySelector('.comparison-chart.mobile-hide')
            : this.querySelector('.comparison-chart.desktop-hide');
        if (!siblingChart || !sourceChart) return null;

        const sourceColumns = Array.from(sourceChart.querySelectorAll('.product-column'));
        const siblingColumns = Array.from(siblingChart.querySelectorAll('.product-column'));
        const posIndex = sourceColumns.indexOf(column);
        if (posIndex === -1 || posIndex >= siblingColumns.length) return null;
        return siblingColumns[posIndex];
    }

    #syncProductToSibling(sourceColumn, productData) {
        if (this.#syncing || !productData?.product) return;
        const sibling = this.#getSiblingColumn(sourceColumn);
        if (!sibling) return;

        this.#syncing = true;
        sibling.dataset.productId = String(productData.product.id);
        sibling.dataset.productHandle = productData.product.handle;
        this.#renderProductInColumn(sibling, productData);
        this.#updateTableRows(sibling, productData);
        this.#syncing = false;
    }

    #syncVariantToSibling(sourceColumn, variantId, variantName, variantIndex) {
        if (this.#syncing) return;
        const sibling = this.#getSiblingColumn(sourceColumn);
        if (!sibling) return;

        this.#syncing = true;
        sibling.querySelectorAll('.na-form__label').forEach((l) => l.classList.remove('selectedColor'));
        const targetLabel = sibling.querySelector(`.na-form__label[data-varid="${variantId}"]`);
        if (targetLabel) {
            targetLabel.classList.add('selectedColor');
            const targetInput = targetLabel.previousElementSibling;
            if (targetInput) targetInput.checked = true;
        }

        const variantText = sibling.querySelector('[ref="variantText"]');
        if (variantText) variantText.textContent = variantName;

        sibling.querySelectorAll('.customize-product__image').forEach((img) => (img.style.display = 'none'));
        const targetImg = sibling.querySelector(`.customize-product__image[data-index-${variantIndex}]`);
        if (targetImg) targetImg.style.display = 'block';

        this.#updatePrice(sibling, variantId);
        sibling.dataset.selectedVariant = variantId;
        this.#syncing = false;
    }

    #updatePrice(column, variantId) {
        const productHandle = column.dataset.productHandle;
        const productId = column.dataset.productId;
        const data = this.#findProductData(productHandle, productId);
        if (!data?.product) return;

        const variant = data.product.variants.find((v) => String(v.id) === String(variantId));
        const variantDiscount = Number(data.variant_metafields?.[variantId]?.discount || 0);
        const productDiscount = Number(data.product_discount || 0);
        const discount = variantDiscount > 0 ? variantDiscount : productDiscount;
        const basePrice = Number(variant?.price || 0);
        const compareAtPrice = Number(variant?.compare_at_price || 0);

        let finalPrice = basePrice;
        let comparePrice = 0;

        if (compareAtPrice > basePrice) {
            finalPrice = basePrice;
            comparePrice = compareAtPrice;
        } else if (discount > 0) {
            finalPrice = basePrice - discount;
            comparePrice = basePrice;
        }

        const priceEl = column.querySelector('[ref="price"]');
        const compareEl = column.querySelector('[ref="comparePrice"]');
        if (priceEl) priceEl.textContent = formatShopMoney(finalPrice);
        if (compareEl) {
            if (comparePrice > 0) {
                compareEl.classList.remove('hidden');
                compareEl.innerHTML = `<s>${formatShopMoney(comparePrice)}</s>`;
            } else {
                compareEl.classList.add('hidden');
            }
        }
    }

    #createSwatchElement(variant, index, handle, data, context = 'desktop') {
        const value = variant.option1;
        let swatchImage = '';

        if (data && data.color_swatch_images) {
            const swatches = data.color_swatch_images.split(',');
            const match = swatches.find((s) => s.startsWith(`${value}:`));
            if (match) {
                swatchImage = match.substring(value.length + 1);
                if (swatchImage === 'null') swatchImage = '';
            }
        }

        if (!swatchImage) {
            const handleized = value
                .toLowerCase()
                .replace(/ /g, '-')
                .replace(/[^\w-]/g, '');
            swatchImage = `/cdn/shop/files/${handleized}.jpg`;
        }

        const div = document.createElement('div');
        div.className = `input-label na-form__field na-form__field--color ${!variant.available ? 'display-none' : ''}`;

        const input = document.createElement('input');
        input.className = 'na-form__input na-form__input--color';
        input.type = 'radio';
        input.name = `${context}-Color-${handle}`;
        input.value = value;
        input.id = `${context}-${handle}-variant-${index}`;
        input.checked = index === 0;
        input.setAttribute('on:change', '/selectVariant');

        const label = document.createElement('label');
        label.htmlFor = input.id;
        label.className = `na-form__label na-form__label--color ${index === 0 ? 'selectedColor' : ''}`;
        label.dataset.varid = variant.id;
        label.dataset.index = index;
        label.dataset.name = value;
        label.style.backgroundImage = `url(${swatchImage})`;

        div.appendChild(input);
        div.appendChild(label);
        return div;
    }

    #getUniqueVariants(product) {
        const unique = {};
        (product.variants || []).forEach((v) => {
            if (!v?.option1 || unique[v.option1]) return;
            unique[v.option1] = v;
        });
        return Object.values(unique);
    }

    #extractImageUrl(variant, data) {
        const metafieldImg = data.variant_metafields?.[variant.id]?.variant_image;
        const url = typeof metafieldImg === 'string' ? metafieldImg : metafieldImg?.url || metafieldImg?.src;
        if (url && !url.includes('error')) return url;

        const variantImg = variant.featured_image?.src || variant.featured_image || variant.image;
        return (typeof variantImg === 'string' ? variantImg : variantImg?.src) || data.product.featured_image || '';
    }
}

if (!customElements.get('pdp-comparison-component')) {
    customElements.define('pdp-comparison-component', PdpComparison);
}
